'use client';

import BranchHeader from '@/app/branch/[branchSlug]/components/branch-header';
import BranchImageGallery from '@/app/branch/[branchSlug]/components/branch-image-gallery';
import Divider from '@/components/divider';
import PageHeader from '@/components/page-header';
import {
  AcademyBreadcrumbItem,
  HomeBreadcrumbItem,
  PricingBreadcrumbItem,
} from '@/data/globals.data';
import { fromSlug } from '@/lib/utils';
import { useAcademyDetail } from '@/services/query/academy.query';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import AcademyBasicCard from './components/academy-basic-card';
import AcademyInfoSection from './components/academy-info-section';
import AcademyNearBranches from './components/academy-near-branches';
import AcademyReviewSection from './components/academy-review-section';

export default function Client() {
  const params = useParams();
  const academySlug = params?.academySlug as string;
  const academyId = fromSlug(academySlug);

  // 훅은 조건 없이 호출한다(잘못된 slug면 enabled:false로 요청만 막는다).
  // 서버가 같은 키로 넣어 둔 상세가 있으면 첫 렌더가 HTML에 그대로 실린다.
  const {
    data: academyEntity,
    isLoading,
    error,
  } = useAcademyDetail(academyId ?? 0);

  if (!academyId) {
    return <div>잘못된 학원입니다.</div>;
  }

  const academy = academyEntity?.data;

  if (isLoading) {
    return <div>로딩중...</div>;
  }
  if (!academy || error) {
    return <div>학원을 찾을 수 없습니다.</div>;
  }

  const hour = academy.academyOperateHour;
  const days = [
    { label: '일요일', open: hour?.sunOpenTime, close: hour?.sunCloseTime },
    { label: '월요일', open: hour?.monOpenTime, close: hour?.monCloseTime },
    { label: '화요일', open: hour?.tueOpenTime, close: hour?.tueCloseTime },
    { label: '수요일', open: hour?.wedOpenTime, close: hour?.wedCloseTime },
    { label: '목요일', open: hour?.thuOpenTime, close: hour?.thuCloseTime },
    { label: '금요일', open: hour?.friOpenTime, close: hour?.friCloseTime },
    { label: '토요일', open: hour?.satOpenTime, close: hour?.satCloseTime },
    {
      label: '공휴일',
      open: hour?.holidayOpenTime,
      close: hour?.holidayCloseTime,
    },
  ];
  const todayIndex = new Date().getDay(); // 0~6
  const hours = days.map(({ label, open, close }, index) => ({
    label,
    time: open && close ? `${open} ~ ${close}` : '',
    isToday: index === todayIndex,
  }));

  return (
    <>
      <div className="desktop:bg-bg-grayblue desktop:h-[230px] desktop:mb-15 relative">
        <div className="desktop:inline-block bg-white-rhombus-pattern desktop:h-[48px] desktop:w-[260px] absolute bottom-0 left-1/2 hidden h-7.5 w-40.5 translate-x-[250px] transform"></div>

        <PageHeader
          breadcrumbItems={[
            HomeBreadcrumbItem,
            AcademyBreadcrumbItem,
            { label: academy.title, href: `/academy/${academySlug}` },
          ]}
        >
          <div className="desktop:mt-10 desktop:mb-2 mx-auto mt-5 w-full">
            {/* 모든 학원에 같은 문구라 h1 이 아니다 — 페이지 h1 은 아래 학원 이름(BranchHeader) 하나다 */}
            <p className="text-level-2 typo-28 desktop:typo-50 font-black">
              <span className="text-font-blue">면허취득</span> 가능한
              운전면허학원
            </p>
          </div>
        </PageHeader>
      </div>
      <div className="desktop:flex-row desktop:justify-center desktop:gap-12 flex flex-col">
        <div className="desktop:flex desktop:w-[504px] desktop:gap-10 hidden flex-col">
          <BranchImageGallery images={academy.photoPaths} />
          <AcademyReviewSection reviews={academy.reviews} />
        </div>
        <div className="desktop:w-[636px]">
          <BranchImageGallery
            images={academy.photoPaths}
            className="desktop:hidden"
          />
          <div className="desktop:hidden pb-7" />
          <BranchHeader name={academy.title} isDirect={false} tags={[]} />
          <div className="desktop:hidden pb-7" />
          <hr className="desktop:hidden text-stroke-white h-[1px]" />
          <div className="desktop:hidden pb-8" />

          <AcademyReviewSection
            reviews={academy.reviews}
            className="desktop:hidden"
          />
          <div className="desktop:pb-9 pb-8" />
          <Divider />
          <div className="desktop:pb-9 pb-8" />

          <AcademyInfoSection
            address={academy.jibunAddress ? [academy.jibunAddress] : []}
            roadAddress={academy.roadAddress || ''}
            hours={hours}
            phoneNumber={academy.phone}
            operatingHoursNote={hour?.notice}
            operatingHoursAddNote="* 운영시간은 변경될 수 있으므로, 정확한 정보는 학원에 문의해 주세요."
            homepage={academy.url}
          />

          {/* 학원 소개글 */}
          {academy.description && (
            <AcademyBasicCard
              iconPath="/images/icons/icon_intro.svg"
              title="학원 소개글"
            >
              <p
                className="desktop:typo-18 text-level-1 desktop:font-medium typo-16 desktop:leading-relaxed leading-relaxed font-semibold"
                dangerouslySetInnerHTML={{
                  __html: academy.description.replace(/\n/g, '<br/>'),
                }}
              ></p>
            </AcademyBasicCard>
          )}

          {academy.description && academy.shuttleBuses?.length > 0 && (
            <Divider className="desktop:my-10 my-6 space-y-7" />
          )}

          {/* 셔틀버스 운행 */}
          {academy.shuttleBuses && academy.shuttleBuses.length > 0 && (
            <AcademyBasicCard
              iconPath="/images/icons/icon_shuttle.svg"
              title="셔틀버스 운행"
            >
              <div className="flex flex-col">
                {academy.shuttleBuses.map((bus, idx) => (
                  <div className="flex gap-1" key={bus.title + '_' + idx}>
                    <span className="desktop:typo-18 text-level-3 desktop:leading-relaxed typo-16 shrink-0 leading-relaxed font-bold">
                      {bus.title} :
                    </span>
                    <span className="desktop:typo-18 text-level-3 desktop:leading-relaxed desktop:font-medium typo-16 leading-relaxed font-semibold">
                      {bus.runDirection}
                      {bus.shuttleBusTimes ? ' - ' : ''}
                      {bus.shuttleBusTimes?.map((time, timeIdx) => (
                        <span key={'time_' + timeIdx}>
                          {time.runDirection}
                          {timeIdx != bus.shuttleBusTimes.length - 1
                            ? ' / '
                            : ''}
                        </span>
                      ))}
                    </span>
                  </div>
                ))}
              </div>
            </AcademyBasicCard>
          )}
        </div>
      </div>

      {/* 면허취득 컨셉 — 학원 반경 20km 안에 드라이빙존이 있으면 그 지점을, 없으면 요금 안내 CTA */}
      {academy.nearStores && academy.nearStores.length > 0 ? (
        <AcademyNearBranches
          academyTitle={academy.title}
          nearStores={academy.nearStores}
        />
      ) : (
        <>
          {/* 면허취득 컨셉 CTA - 웹 */}
          {/* 문구는 한 덩어리로 묶어 한 줄로 둔다 — 조각마다 flex 항목이 되면 버튼 폭에 밀려 제각각 두 줄로 접힌다 */}
          <div className="desktop:flex border-stroke-grayblue typo-20 text-level-2 web-w mx-auto mt-20 hidden h-[111px] items-center justify-center gap-4 border px-8 font-bold">
            <p className="whitespace-nowrap">
              학원 가기 전{' '}
              <span className="text-font-blue">드라이빙존 시뮬레이터</span>로 감
              잡고 등록하세요! 학원에서 부족한 연습도 채울 수 있어요.
            </p>
            <Link
              href={PricingBreadcrumbItem.href}
              className="bg-main-blue typo-18 inline-flex h-[60px] shrink-0 items-center rounded-xl px-6 font-bold whitespace-nowrap text-white shadow-[0_2px_0_#001737] transition hover:brightness-110"
            >
              시뮬레이터 요금 보기
            </Link>
          </div>
          {/* 면허취득 컨셉 CTA - 모바일 */}
          <div className="desktop:hidden border-stroke-grayblue typo-18 text-level-2 mobile-w mt-[30px] flex flex-col items-center justify-center gap-[10px] border pt-[22px] pb-[14px] font-bold">
            <div>
              학원 가기 전{' '}
              <span className="text-font-blue">드라이빙존 시뮬레이터</span>로
            </div>
            <div>감 잡고 등록하세요!</div>
            <div>학원에서 부족한 연습도 채울 수 있어요.</div>
            <Link
              href={PricingBreadcrumbItem.href}
              className="bg-main-blue typo-16 mt-1 inline-flex h-[43px] items-center rounded-lg px-5 font-bold text-white shadow-[0_2px_0_#001737]"
            >
              시뮬레이터 요금 보기
            </Link>
          </div>
        </>
      )}
    </>
  );
}
